import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Close, Edit } from "@mui/icons-material";
import { ILocationResult, LocationSearch } from "./Search";
import { Box, Paper, Typography, Chip, IconButton, Button } from "@mui/material";

interface IWaypoint {
    add: string;
    lat: number;
    lng: number;
}

interface ILocationData {
    add: string | null;
    lat: string | null;
    lng: string | null;
}

interface LocationSelectorProps {
    waypoints: IWaypoint[];
    selected: string | null;
    onAddWaypoint: () => void;
    drop: ILocationData | null;
    pickup: ILocationData | null;
    onRemoveWaypoint: (index: number) => void;
    onSelectWaypoint: (index: number) => void;
    onSelectDrop: (location: ILocationResult) => void;
    onSelectPickup: (location: ILocationResult) => void;
    onUpdateWaypoint: (index: number, location: ILocationResult) => void;
}

export const LocationSelector = ({
    drop,
    pickup,
    selected,
    onSelectDrop,
    onAddWaypoint,
    waypoints = [],
    onSelectPickup,
    onRemoveWaypoint,
    onSelectWaypoint,
    onUpdateWaypoint,
}: LocationSelectorProps) => {
    const { t } = useTranslation();
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchType, setSearchType] = useState<'pickup' | 'drop' | 'waypoint'>('pickup');
    const [searchWaypointIndex, setSearchWaypointIndex] = useState<number | undefined>(undefined);

    const hasPickupAndDrop = !!pickup?.add && !!drop?.add;
    const MAX_WAYPOINTS = 24;

    const getLetter = (index: number) => String.fromCharCode(66 + index);

    const isSelected = (type: string, index?: number) => {
        if (type === 'pickup') return selected === 'pickup';
        if (type === 'drop') return selected === 'drop';
        if (type === 'waypoint' && index !== undefined) {
            return selected === 'waypoints';
        }
        return false;
    };

    const handleOpenSearch = (type: 'pickup' | 'drop' | 'waypoint', index?: number) => {
        setSearchType(type);
        setSearchWaypointIndex(index);
        setSearchOpen(true);
    };

    const handleSelectLocation = (location: ILocationResult) => {
        if (searchType === 'pickup') {
            onSelectPickup(location);
        } else if (searchType === 'drop') {
            onSelectDrop(location);
        } else if (searchType === 'waypoint' && searchWaypointIndex !== undefined) {
            onUpdateWaypoint(searchWaypointIndex, location);
        }
        setSearchOpen(false);
        setSearchWaypointIndex(undefined);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper
                onClick={() => handleOpenSearch('pickup')}
                sx={{
                    p: 4,
                    mb: 2,
                    gap: 1,
                    display: 'flex',
                    cursor: 'pointer',
                    alignItems: 'center',
                    borderColor: 'primary.main',
                    bgcolor: isSelected('pickup') ? 'primary.light' : 'background.default',
                }}
            >
                <Box
                    sx={{
                        width: 30,
                        height: 30,
                        lineHeight: 0,
                        display: 'flex',
                        fontWeight: 'bold',
                        borderRadius: '50%',
                        alignItems: 'center',
                        color: 'common.white',
                        bgcolor: 'success.main',
                        justifyContent: 'center',
                    }}
                >
                    A
                </Box>
                <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                        {t("ADM_PICKUP")}
                    </Typography>
                    <Typography variant="body2">
                        {pickup?.add || t("ADM_SELECT_PICKUP_LOCATION")}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenSearch('pickup'); }}>
                    <Edit fontSize="small" />
                </IconButton>
                {isSelected('pickup') && <Chip label={t("ADM_SELECTED")} color="primary" size="small" />}
            </Paper>

            {waypoints.map((wp, index) => (
                <Paper
                    key={index}
                    onClick={() => onSelectWaypoint(index)}
                    sx={{
                        p: 4,
                        mb: 2,
                        gap: 1,
                        display: 'flex',
                        cursor: 'pointer',
                        alignItems: 'center',
                        borderColor: 'primary.main',
                        bgcolor: isSelected('waypoint', index) ? 'primary.light' : 'background.default',
                    }}
                >
                    <Box
                        sx={{
                            width: 30,
                            height: 30,
                            fontSize: 12,
                            display: 'flex',
                            fontWeight: 'bold',
                            borderRadius: '50%',
                            bgcolor: 'info.main',
                            alignItems: 'center',
                            color: 'common.white',
                            justifyContent: 'center',
                        }}
                    >
                        {getLetter(index)}
                    </Box>
                    <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">
                            {t("ADM_WAYPOINT")} {index + 1}
                        </Typography>
                        <Typography variant="body2">
                            {wp.add || t("ADM_SELECT_WAYPOINT_LOCATION")}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemoveWaypoint(index);
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenSearch('waypoint', index); }}>
                        <Edit fontSize="small" />
                    </IconButton>
                    {isSelected('waypoint', index) && <Chip label={t("ADM_SELECTED")} color="primary" size="small" />}
                </Paper>
            ))}

            {hasPickupAndDrop && waypoints.length < MAX_WAYPOINTS && (
                <Button
                    fullWidth
                    sx={{ mb: 1 }}
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={onAddWaypoint}
                >
                    {t("ADM_ADD_MULTIPLE_STOPS")}
                </Button>
            )}

            <Paper
                onClick={() => handleOpenSearch('drop')}
                sx={{
                    p: 4,
                    mb: 2,
                    gap: 1,
                    display: 'flex',
                    cursor: 'pointer',
                    alignItems: 'center',
                    borderColor: 'primary.main',
                    bgcolor: isSelected('drop') ? 'primary.light' : 'background.default',
                }}
            >
                <Box
                    sx={{
                        width: 30,
                        height: 30,
                        display: 'flex',
                        borderRadius: 1,
                        fontWeight: 'bold',
                        alignItems: 'center',
                        bgcolor: 'error.main',
                        color: 'common.white',
                        justifyContent: 'center',
                    }}
                >
                    {getLetter(waypoints.length)}
                </Box>
                <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                        {t("ADM_DROP")}
                    </Typography>
                    <Typography variant="body2">
                        {drop?.add || t("ADM_SELECT_DROP_LOCATION")}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenSearch('drop'); }}>
                    <Edit fontSize="small" />
                </IconButton>
                {isSelected('drop') && <Chip label={t("ADM_SELECTED")} color="primary" size="small" />}
            </Paper>

            <LocationSearch
                open={searchOpen}
                type={searchType}
                onSelect={handleSelectLocation}
                onClose={() => setSearchOpen(false)}
                placeholder={t("ADM_SEARCH_FOR_ADDRESS")}
            />
        </Box>
    );
};